import React, { useEffect } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useLocation } from "react-router";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { useForm } from "@/src/provider/Form/FormProvider";
import { getCaseLink } from "@/src/services/productService";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import InsuranceData from "@/src/components/Form/Settlement/Summary/InsuranceData";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import EmailVerificationModal from "@/src/components/FormInfoModal/EmailVerificationModal";
import summaryStyle from "@/src/components/Form/ContractLaw/Summary/summaryStyle";
import SummaryDetailsItem from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsItem";
import SummaryDetailsPersonItems from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsPersonItems";

type SummaryProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

const Summary = ({ product, submitProps }: SummaryProps) => {
  const location = useLocation();
  const stateField = location.state?.field;
  const { values, handleSubmit } = useForm();

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  return (
    <ContentBox>
      <Typography variant={"h5"}>
        Ihre Angaben
      </Typography>
      <Grid container>
        <Box id={"accidentBase"} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Angaben zum Unfall</Typography>
        </Grid>
        <SummaryDetailsPersonItems basePath={"customer.person"} caseData={product} />
        <SummaryDetailsItem fieldPath={"customer.telephone"} caseData={product} />
        <SummaryDetailsItem fieldPath={"client.bankData.iban"} caseData={product} />
        <SummaryDetailsItem fieldPath={"typeOfDamage"} caseData={product} />
        {["vehicle", "vehicleAndPerson"].includes(values.typeOfDamage) && (
          <SummaryDetailsItem fieldPath={"clientIsVehicleOwner"} caseData={product} fieldType={"yesNo"} />
        )}
        <SummaryDetailsItem fieldPath={"typeOfAccident"} caseData={product} />
        <SummaryDetailsItem fieldPath={"courseOfEvents"} caseData={product} />
        <SummaryDetailsItem fieldPath={"accidentDate"} caseData={product} fieldType={"date"} />
        <SummaryDetailsItem fieldPath={"accidentTime"} caseData={product} fieldType={"time"} />
        <SummaryDetailsItem fieldPath={"accidentLocalityDescription"} caseData={product} />
        <SummaryDetailsItem fieldPath={"accidentAddress.streetAddress"} caseData={product} />
        <SummaryDetailsItem fieldPath={"accidentAddress.postalCode"} caseData={product} />
        <SummaryDetailsItem fieldPath={"accidentAddress.addressLocality"} caseData={product} />
        <SummaryDetailsItem fieldPath={"policeInvolved"} caseData={product} fieldType={"yesNo"} />

        <Box id={"opposingParty"} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Angaben zum Unfallgegner</Typography>
        </Grid>
        <SummaryDetailsItem fieldPath={"licensePlateOpposingParty"} caseData={product} />
        {values.opposingPartyType === "opposingPartyPerson" && <SummaryDetailsPersonItems basePath={"opposingPartyPerson"} caseData={product} />}
        {values.opposingPartyType === "opposingPartyLegalEntity" && (
          <>
            <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.legalName"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.address.streetAddress"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.address.postalCode"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.address.addressLocality"} caseData={product} />
          </>
        )}
        {values.opposingPartyType !== "null" && (
          <>
            <SummaryDetailsItem fieldPath={"opposingCarLiabilityInsurance.organization.legalName"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opposingInsurancePolicyNumber"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opposingCaseReferenceNumber"} caseData={product} />
          </>
        )}

        <Box id={"damage"} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Details zum Schaden</Typography>
        </Grid>
        {["vehicle", "vehicleAndPerson"].includes(product.typeOfDamage) && (
          <>
            <SummaryDetailsItem fieldPath={"driverOfClientVehicle"} caseData={product} />
            {values.driverOfClientVehicle === "other" && <SummaryDetailsPersonItems basePath={"driverPerson"} caseData={product} />}
            <SummaryDetailsItem fieldPath={"licensePlateClientVehicle"} caseData={product} />
            <SummaryDetailsItem fieldPath={"vehicleMake"} caseData={product} />
            <SummaryDetailsItem fieldPath={"vehicleModel"} caseData={product} />
            <SummaryDetailsItem fieldPath={"vehicleId"} caseData={product} />
            <SummaryDetailsItem fieldPath={"damageEstimationDocumentType"} caseData={product} />
            {["person", "vehicleAndPerson"].includes(product.typeOfDamage) && (
              <>
                <SummaryDetailsItem fieldPath={"descriptionInjuries"} caseData={product} />
              </>
            )}
            {product.typeOfDamage === "other" && (
              <>
                <SummaryDetailsItem fieldPath={"descriptionDamage"} caseData={product} />
              </>
            )}
          </>
        )}

        <Box id={"insuranceData"} />
        <InsuranceData title={"Versicherung"} product={product} />
      </Grid>
      <form onSubmit={handleSubmit}>
        <FormSubmit
          {...submitProps}
          hideBackButton={false}
          backLink={getCaseLink(product) + "/antrag/unfall"}
          backText={"Alles bearbeiten"}
          nextText={"Angaben speichern"}
        />
      </form>
      <EmailVerificationModal />
    </ContentBox>
  );
};

export default Summary;
